import React, { useEffect, useState } from 'react';
import { Search, X, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface AdvancedSearchProps {
  onResultClick: () => void;
}
export function AdvancedSearch({
  onResultClick
}: AdvancedSearchProps) {
  const [companyName, setCompanyName] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  useEffect(() => {
    const targetText = 'Dunder Mifflin';
    let currentIndex = 0;
    // Start typing after a short delay
    const startDelay = setTimeout(() => {
      const typingInterval = setInterval(() => {
        if (currentIndex <= targetText.length) {
          setCompanyName(targetText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          // Trigger search after typing finishes
          setTimeout(() => {
            setIsSearching(true);
            setTimeout(() => {
              setIsSearching(false);
              setShowResults(true);
            }, 800);
          }, 400);
        }
      }, 80); // Typing speed
      return () => clearInterval(typingInterval);
    }, 500);
    return () => clearTimeout(startDelay);
  }, []);
  return <div className="px-8 py-6 max-w-6xl mx-auto w-full" data-id="element-258">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-259">
        Advanced Company Search
      </h1>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-260">
        <div className="p-6 space-y-4" data-id="element-261">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-262">
            {/* Company Name */}
            <div className="relative" data-id="element-263">
              <label className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-indigo-600 z-10" data-id="element-264">
                Company Name *
              </label>
              <div className="relative" data-id="element-265">
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className="w-full px-4 py-3 border-2 border-indigo-500 rounded-lg focus:outline-none text-gray-900" data-id="element-266" />
                {companyName && <button onClick={() => setCompanyName('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-267">
                    <X className="w-5 h-5" data-id="element-268" />
                  </button>}
              </div>
            </div>

            {/* Company Website */}
            <div data-id="element-269">
              <input type="text" placeholder="Company Website" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-270" />
            </div>

            {/* Country */}
            <div className="relative" data-id="element-271">
              <label className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-gray-500 z-10" data-id="element-272">
                Country
              </label>
              <div className="relative" data-id="element-273">
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 appearance-none bg-white" data-id="element-274">
                  <option data-id="element-275">United States</option>
                  <option data-id="element-276">Canada</option>
                  <option data-id="element-277">United Kingdom</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-gray-400 pointer-events-none" data-id="element-278">
                  <X className="w-4 h-4" data-id="element-279" />
                  <ChevronDown className="w-4 h-4" data-id="element-280" />
                </div>
              </div>
            </div>

            {/* State */}
            <div className="relative" data-id="element-281">
              <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 appearance-none bg-white" data-id="element-282">
                <option value="" disabled selected hidden data-id="element-283">
                  State
                </option>
                <option data-id="element-284">Pennsylvania</option>
                <option data-id="element-285">Connecticut</option>
                <option data-id="element-286">New York</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-287" />
            </div>
          </div>

          {/* Street Address */}
          <div className="relative" data-id="element-288">
            <input type="text" placeholder="Street Address" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-289" />
            <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-290" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-291">
            {/* City */}
            <div className="relative" data-id="element-292">
              <input type="text" placeholder="City" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-293" />
              <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-294" />
            </div>

            {/* Postal Code */}
            <div className="relative" data-id="element-295">
              <input type="text" placeholder="Postal Code" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-296" />
              <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-297" />
            </div>
          </div>

          {/* Search Button */}
          <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 transition-colors mt-2" disabled={isSearching} data-id="element-298">
            {isSearching ? <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" data-id="element-299" /> : <>
                <Search className="w-5 h-5" data-id="element-300" />
                SEARCH
              </>}
          </button>
        </div>

        {/* Help Section */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between text-sm" data-id="element-301">
          <div className="text-gray-600" data-id="element-302">
            Need help finding a company?{' '}
            <a href="#" className="text-indigo-600 hover:underline" data-id="element-303">
              Contact search support
            </a>
          </div>
          <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-304" />
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence data-id="element-305">
        {showResults && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-306">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-6 py-3 flex items-center justify-between" data-id="element-307">
              <span className="text-gray-700" data-id="element-308">Showing results 1 to 2</span>
              <div className="flex items-center gap-2" data-id="element-309">
                <button className="px-3 py-1.5 text-sm text-gray-400 flex items-center gap-1 cursor-not-allowed" data-id="element-310">
                  <ChevronLeft className="w-4 h-4" data-id="element-311" />
                  PREVIOUS PAGE
                </button>
                <button className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded flex items-center gap-1 hover:bg-indigo-50" data-id="element-312">
                  NEXT PAGE
                  <ChevronRight className="w-4 h-4" data-id="element-313" />
                </button>
              </div>
            </div>

            {/* Result 1 - HQ (Clickable) */}
            <motion.div whileHover={{
          scale: 1.005
        }} onClick={onResultClick} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all relative overflow-hidden group" data-id="element-314">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500" data-id="element-315" />
              <div className="pl-4" data-id="element-316">
                <div className="flex items-center gap-2 mb-1" data-id="element-317">
                  <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600 transition-colors" data-id="element-318">
                    DUNDER MIFFLIN PAPER COMPANY
                  </h3>
                  <ExternalLink className="w-4 h-4 text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-319" />
                </div>
                <p className="text-indigo-600 text-sm mb-3" data-id="element-320">
                  1725 SLOUGH AVENUE, SCRANTON, PA, 18505
                </p>
                <div className="flex items-center justify-between" data-id="element-321">
                  <div className="flex items-center gap-2" data-id="element-322">
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-323">
                      Active
                    </span>
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-324">
                      HQ
                    </span>
                  </div>
                  <button onClick={e => {
                e.stopPropagation();
                onResultClick();
              }} className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-325">
                    View
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Result 2 - Branch */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 relative overflow-hidden" data-id="element-326">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500" data-id="element-327" />
              <div className="pl-4" data-id="element-328">
                <div className="flex items-center gap-2 mb-1" data-id="element-329">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-330">
                    DUNDER MIFFLIN STAMFORD
                  </h3>
                </div>
                <p className="text-indigo-600 text-sm mb-3" data-id="element-331">
                  850 CANAL ST, STAMFORD, CT, 06902
                </p>
                <div className="flex items-center justify-between" data-id="element-332">
                  <div className="flex items-center gap-2" data-id="element-333">
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-334">
                      Active
                    </span>
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-335">
                      Branch
                    </span>
                  </div>
                  <button onClick={onResultClick} className="px-5 py-2 bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 text-sm font-medium rounded-lg transition-colors" data-id="element-336">
                    Add
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}